<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    
    <!-- Environment Badge - nur anzeigen, wenn nicht beide auf production sind -->
    <div v-if="shouldShowBadge" class="environment-badge">
      <b-badge :variant="environmentBadgeVariant" pill>
        {{ environmentName }}
      </b-badge>
    </div>
    
    <!-- Rest des Templates -->
    <component :is="layout">
      <router-view />
    </component>
    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import { BBadge } from 'bootstrap-vue'

// Config importieren
import config from '@/config'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, computed } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () =>
  import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    // Layouts
    LayoutVertical,
    LayoutHorizontal,
    LayoutFull,
    ScrollToTop,
    // Badge-Komponente
    BBadge,
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    // Prüfen, ob das Badge angezeigt werden soll
    shouldShowBadge() {
      // Beide auf Produktion? Dann kein Badge anzeigen
      const bothProd = this.nodeEnvShort === 'prod' && this.vueAppEnvShort === 'prod';
      return !bothProd;
    },
    // Umgebungsvariablen gekürzt
    nodeEnvShort() {
      const env = process.env.NODE_ENV || 'undef';
      return env === 'development' ? 'dev' : (env === 'production' ? 'prod' : env);
    },
    vueAppEnvShort() {
      const env = process.env.VUE_APP_ENV || 'undef';
      return env === 'development' ? 'dev' : (env === 'production' ? 'prod' : env);
    },
    // Umgebungserkennung - prüft beide Variablen
    isDevelopment() {
      return process.env.NODE_ENV === 'development' || 
             process.env.VUE_APP_ENV === 'development';
    },
    // Detaillierter Text für das Badge
    environmentName() {
      return `n:${this.nodeEnvShort} a:${this.vueAppEnvShort}`;
    },
    // Dynamische Farbe für das Badge
    environmentBadgeVariant() {
      const bothDev = this.nodeEnvShort === 'dev' && this.vueAppEnvShort === 'dev';
      const bothProd = this.nodeEnvShort === 'prod' && this.vueAppEnvShort === 'prod';
      const mixed = !bothDev && !bothProd;
      
      if (bothDev) return 'warning';     // Gelb für reine Entwicklung
      if (bothProd) return 'success';    // Grün für reine Produktion
      return 'primary';                  // Blau für gemischte Modi
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark',
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },

  created() {
    this.$store.dispatch('getStorageTypesEntries')
    this.$store.dispatch('getCartEntries')
    this.$store.dispatch('getLocationsEntries')
  },

  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
}
</script>

<style>
.environment-badge {
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 9999 !important; /* Höchste Priorität */
  padding: 5px;
  opacity: 0.9;
  transition: opacity 0.3s ease;
  display: block !important;
  pointer-events: all !important;
  visibility: visible !important;
}

.environment-badge:hover {
  opacity: 1;
  transform: scale(1.05);
}

/* Für bessere Sichtbarkeit des Badge-Textes */
.environment-badge .badge {
  font-size: 0.85rem;
  font-weight: bold;
  padding: 5px 10px;
  text-shadow: 0px 0px 1px rgba(0,0,0,0.3);
}
</style>
